import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveSport, toSlug } from '@/shell/sports-menu-utils';
import { EmptyState } from '@/ui';
import { EventsFeed, EventsFeedSkeleton } from './-components/EventsFeed';

export const Route = createFileRoute('/sports/$sport/topevents')({
  component: TopEventsPage,
});

function TopEventsPage() {
  const { sport: sportSlug } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());

  const sport = useMemo(
    () => resolveSport(menuQuery.data, sportSlug),
    [menuQuery.data, sportSlug],
  );

  const filter = useMemo(
    () => (sport ? { Sport: sport.Name, EventIds: [] as number[] } : null),
    [sport],
  );

  if (menuQuery.isPending) {
    return <EventsFeedSkeleton />;
  }

  if (!sport || !filter) {
    return (
      <EmptyState
        icon="football"
        title={t('SportNotFound', { defaultValue: 'Sport not found' })}
        description={t('SportNotInMenu', {
          defaultValue: 'This sport is not in the current sports menu.',
        })}
      />
    );
  }

  return (
    <EventsFeed
      scope="top-events"
      filter={filter}
      title={t('Topevents', { defaultValue: 'Top Events' })}
      subtitle={translateSportsLabel(i18n, t, sport.Id, sport.Name)}
      emptyDescription={t('NoTopEventsForSport', {
        defaultValue: 'No top events available for this sport right now.',
      })}
      onOpenEvent={({ eventId, sport: eventSport, category, tournament, offer }) => {
        const resolvedSport = eventSport ? toSlug(eventSport) : sportSlug;
        const categorySlug = category ? toSlug(category) : undefined;
        const tournamentSlug = tournament ? toSlug(tournament) : undefined;
        if (!categorySlug || !tournamentSlug) return;
        void navigate({
          to: '/sports/$sport/$category/$tournament/$eventId',
          params: {
            sport: resolvedSport,
            category: categorySlug,
            tournament: tournamentSlug,
            eventId: String(eventId),
          },
          search: { offer },
        });
      }}
    />
  );
}
